/**
 * Compact "taste match" badge shown alongside events across the app.
 *
 * Renders a small color-coded pill with the match percentage (e.g. `72%`).
 * The pill is *not* color-only — the percentage number carries the meaning —
 * but the color (green / blue / grey) and an accompanying tooltip reinforce the
 * strength (Great / Good / Match) plus the top reasons.
 *
 * Returns `null` when there is no meaningful match, so callers can render it
 * unconditionally: `<MatchBadge match={match(e)} />`.
 */
import { formatTasteReason } from '../lib/tasteScore';
import { matchLevelLabel, type TasteMatch } from '../lib/tasteMatch';

export interface MatchBadgeProps {
  match: TasteMatch | null;
  /** Extra classes (e.g. the row-chip class used in the compact list). */
  className?: string;
  /** When true, render the level word next to the percentage (used in the
   *  event modal header where there's room). Defaults to false (percent only). */
  showLabel?: boolean;
}

export default function MatchBadge({ match, className, showLabel = false }: MatchBadgeProps) {
  if (!match) return null;
  const { percent, level, reasons } = match;
  const label = matchLevelLabel(level);
  const reasonText = reasons.length > 0
    ? reasons.map(formatTasteReason).join(' · ')
    : 'Based on events you\u2019ve marked as your taste';
  return (
    <span
      className={`badge mh-match mh-match--${level}${className ? ` ${className}` : ''}`}
      title={`${label} (${percent}%) — ${reasonText}`}
    >
      <i className="bi bi-stars me-1" aria-hidden="true" />
      <span className="visually-hidden">{label}: </span>
      {percent}%
      {showLabel && <span className="ms-1">{label}</span>}
    </span>
  );
}
